import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import ProtectDashboardRoute from "../components/ProtectDashboardRoute";
import config from "../config";

export default function PaymentSuccess() {
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [verificationStatus, setVerificationStatus] = useState(
    "Verifying your payment...",
  );

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    // Squad might return different parameter names
    const reference = query.get("reference") || query.get("transaction_ref");
    const squadRef = query.get("squad_transaction_ref");

    console.log("📍 PaymentSuccess mounted");
    console.log("🔍 URL params:", {
      reference,
      squadRef,
      fullUrl: window.location.href,
      search: location.search,
    });

    if (!reference && !squadRef) {
      console.error("❌ No payment reference found in URL");
      toast.error("No payment reference found");
      navigate("/wallet");
      return;
    }

    // Use the reference that's available
    const paymentReference = reference || squadRef;
    console.log("📦 Using payment reference:", paymentReference);

    async function verify(ref) {
      try {
        setVerificationStatus("Verifying your payment with Squad...");
        console.log("🔄 Calling verification API for ref:", ref);

        const res = await axios.get(
          `${config.API_BASE_URL}/api/verify/${ref}`,
          {
            withCredentials: true,
          },
        );

        console.log("✅ Verification response:", res.data);

        if (res.data.status === "success") {
          toast.success(res.data.message || "Wallet credited successfully!");
          setVerificationStatus(
            "Payment verified! Redirecting to dashboard...",
          );

          // Store success message in sessionStorage to show on dashboard
          sessionStorage.setItem("payment_success", "true");
          sessionStorage.setItem("payment_amount", res.data.data?.balance);

          setTimeout(() => {
            navigate("/dashboard");
          }, 2000);
        } else {
          throw new Error(res.data.error || "Verification failed");
        }
      } catch (err) {
        console.error(
          "❌ Verification error:",
          err.response?.data || err.message,
        );

        // Check if it's a duplicate transaction
        if (err.response?.data?.message?.includes("Already funded")) {
          toast.success("Payment already processed!");
          setTimeout(() => {
            navigate("/dashboard");
          }, 2000);
        } else {
          // Log the full error for debugging
          console.error("Full error response:", err.response);

          toast.error(
            err.response?.data?.error ||
              "Payment verification failed. Please contact support.",
          );
          setTimeout(() => {
            navigate("/dashboard");
          }, 3000);
        }
      } finally {
        setLoading(false);
      }
    }

    verify(paymentReference);
  }, [location, navigate]);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
      <ProtectDashboardRoute />

      <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full mx-4">
        {loading ? (
          <div className="text-center">
            {/* Loading Spinner */}
            <div className="relative mb-6">
              <div className="w-20 h-20 border-4 border-gray-200 border-t-[#007AFF] rounded-full animate-spin mx-auto"></div>
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-3">
              {verificationStatus}
            </h2>

            <div className="space-y-2 text-gray-600">
              <p>Please don't close this window.</p>
              <p className="text-sm">
                This may take a few moments while we confirm your payment with
                Squad.
              </p>
            </div>

            {/* Progress bar */}
            <div className="mt-6 h-2 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#007AFF] rounded-full animate-pulse"
                style={{ width: "60%" }}
              ></div>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <div className="mb-6">
              {verificationStatus.includes("verified") ||
              verificationStatus.includes("success") ? (
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <svg
                    className="w-10 h-10 text-green-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M5 13l4 4L19 7"
                    ></path>
                  </svg>
                </div>
              ) : (
                <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
                  <svg
                    className="w-10 h-10 text-yellow-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    ></path>
                  </svg>
                </div>
              )}
            </div>

            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              {verificationStatus}
            </h2>

            <p className="text-gray-500">
              Redirecting you to your dashboard...
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
